import { z } from "zod";
import { CommonModels } from "@/data/common/common.models";

export namespace ProfileModels {
/** 
 * UserDtoSchema 
 * @type { object }
 * @property { string } id  
 * @property { string } email Email from user 
 * @property { string } name Name from user 
 * @property { string } firstName  
 * @property { string } lastName  
 */
export const UserDtoSchema = z.object({ id: z.string(), email: z.string().nullable(), name: z.string().nullable(), firstName: z.string().nullable(), lastName: z.string().nullable() });
export type UserDto = z.infer<typeof UserDtoSchema>;


/** 
 * ProfileFieldVisibilityDtoSchema 
 * @type { object }
 * @property { boolean } lossdog_value Visibility setting for lossdog value section. Default: `true` 
 * @property { boolean } introduction Visibility setting for introduction section. Default: `true` 
 * @property { boolean } featured_projects Visibility setting for featured projects section. Default: `true` 
 * @property { boolean } skills Visibility setting for skills section. Default: `true` 
 * @property { boolean } work_experience Visibility setting for work experience section. Default: `true` 
 * @property { boolean } education Visibility setting for education section. Default: `true` 
 * @property { boolean } certifications Visibility setting for certifications section. Default: `true` 
 * @property { boolean } volunteer_community Visibility setting for volunteer community section. Default: `true` 
 * @property { boolean } recognition Visibility setting for recognition section. Default: `true` 
 * @property { boolean } endorsements Visibility setting for endorsements section. Default: `true` 
 * @property { object } labels Human-readable labels for each visibility field. Default: `[object Object]`. Example: `[object Object]` 
 * @property { string } labels.[key]  
 */
export const ProfileFieldVisibilityDtoSchema = z.object({ lossdog_value: z.boolean().default(true), introduction: z.boolean().default(true), featured_projects: z.boolean().default(true), skills: z.boolean().default(true), work_experience: z.boolean().default(true), education: z.boolean().default(true), certifications: z.boolean().default(true), volunteer_community: z.boolean().default(true), recognition: z.boolean().default(true), endorsements: z.boolean().default(true), labels: z.object({}).catchall(z.string()).default({"lossdog_value":"Lossdog Value","introduction":"Introduction","featured_projects":"Featured Projects","skills":"Skills","work_experience":"Work Experience","education":"Education","certifications":"Certifications","volunteer_community":"Volunteer and Community","recognition":"Recognitions","endorsements":"Endorsements"}) });
export type ProfileFieldVisibilityDto = z.infer<typeof ProfileFieldVisibilityDtoSchema>;


/** 
 * FullProfileResponseDtoSchema 
 * @type { object }
 * @property { string } id  
 * @property { string } userId  
 * @property { UserDto } user  
 * @property { string } onboardingStep  
 * @property { string } profilePictureId  
 * @property { string } profilePictureUrl  
 * @property { string } introductionCoverImageId  
 * @property { string } introductionCoverImageUrl  
 * @property { string } introductionVideoUrl  
 * @property { string } introductionVideoThumbnailUrl  
 * @property { string } introductionTitle  
 * @property { string } introductionBio  
 * @property { string } jobPosition  
 * @property { string } jobTitle  
 * @property { number } jobSalary  
 * @property { string } city  
 * @property { string } state  
 * @property { CommonModels.ProfileStatusEnum } status  
 * @property { string } stateReason  
 * @property { CommonModels.ProfileV2DataDto } dataV02  
 * @property { string } generatedAt  
 * @property { ProfileFieldVisibilityDto } fieldVisibility  
 */
export const FullProfileResponseDtoSchema = z.object({ id: z.string(), userId: z.string(), user: UserDtoSchema, onboardingStep: CommonModels.OnboardingStepEnumSchema.nullish(), profilePictureId: z.string().nullable(), profilePictureUrl: z.string().nullable(), introductionCoverImageId: z.string().nullable(), introductionCoverImageUrl: z.string().nullable(), introductionVideoUrl: z.string().nullish(), introductionVideoThumbnailUrl: z.string().nullish(), introductionTitle: z.string().nullish(), introductionBio: z.string().nullish(), jobPosition: z.string().nullish(), jobTitle: z.string().nullish(), jobSalary: z.number().nullish(), city: z.string().nullish(), state: z.string().nullish(), status: CommonModels.ProfileStatusEnumSchema.nullish(), stateReason: z.string().nullish(), dataV02: CommonModels.ProfileV2DataDtoSchema.nullish(), generatedAt: z.iso.datetime({ offset: true }).nullish(), fieldVisibility: ProfileFieldVisibilityDtoSchema });
export type FullProfileResponseDto = z.infer<typeof FullProfileResponseDtoSchema>;


/** 
 * UpdateProfileDtoSchema 
 * @type { object }
 * @property { string } jobPosition  
 * @property { string } introductionTitle  
 * @property { string } introductionBio  
 * @property { string } introductionCoverImageId  
 * @property { string } introductionVideoUrl  
 * @property { CommonModels.ProfileV2DataUpdateDto } dataV02  
 * @property { string } firstName  
 * @property { string } lastName  
 */
export const UpdateProfileDtoSchema = z.object({ jobPosition: z.string().nullish(), introductionTitle: z.string().nullish(), introductionBio: z.string().nullish(), introductionCoverImageId: z.string().nullish(), introductionVideoUrl: z.string().nullish(), dataV02: CommonModels.ProfileV2DataUpdateDtoSchema, firstName: z.string().nullish(), lastName: z.string().nullish() });
export type UpdateProfileDto = z.infer<typeof UpdateProfileDtoSchema>;


/** 
 * UpdateIntroductionSocialMediaDtoSchema 
 * @type { object }
 * @property { string } linkedin  
 * @property { string } instagram  
 * @property { string } youtube  
 * @property { string } facebook  
 */
export const UpdateIntroductionSocialMediaDtoSchema = z.object({ linkedin: z.string().nullable(), instagram: z.string().nullable(), youtube: z.string().nullable(), facebook: z.string().nullable() });
export type UpdateIntroductionSocialMediaDto = z.infer<typeof UpdateIntroductionSocialMediaDtoSchema>;


/** 
 * UpdateIntroductionDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } videoUrl  
 * @property { string } introduction  
 * @property { string } email  
 * @property { string } phoneNumber  
 * @property { string } phoneCountry Min Length: `2`. Max Length: `2` 
 * @property { UpdateIntroductionSocialMediaDto } socialMedia  
 * @property { string } introductionCoverImageId Media Id 
 */
export const UpdateIntroductionDtoSchema = z.object({ title: z.string().nullable(), videoUrl: z.string().nullable(), introduction: z.string().nullable(), email: z.string().nullable(), phoneNumber: z.string().nullable(), phoneCountry: z.string().min(2).max(2).nullable(), socialMedia: UpdateIntroductionSocialMediaDtoSchema.nullable(), introductionCoverImageId: z.uuid().nullable() }).partial();
export type UpdateIntroductionDto = z.infer<typeof UpdateIntroductionDtoSchema>;


/** 
 * ProfileVisibilityFieldSchema 
 * @type { enum }
 */
export const ProfileVisibilityFieldSchema = z.enum(["lossdog_value", "introduction", "featured_projects", "skills", "work_experience", "education", "certifications", "volunteer_community", "recognition", "endorsements"]);
export type ProfileVisibilityField = z.infer<typeof ProfileVisibilityFieldSchema>;
export const ProfileVisibilityField = ProfileVisibilityFieldSchema.enum;

/** 
 * ProfileFieldChangeVisibilityRequestSchema 
 * @type { object }
 * @property { ProfileVisibilityField } field The field to change the visibility of 
 * @property { boolean } visible Whether the field should be visible 
 */
export const ProfileFieldChangeVisibilityRequestSchema = z.object({ field: ProfileVisibilityFieldSchema, visible: z.boolean() });
export type ProfileFieldChangeVisibilityRequest = z.infer<typeof ProfileFieldChangeVisibilityRequestSchema>;


/** 
 * MediaResponseSchema 
 * @type { object }
 * @property { string } resourceName Resource name. Example: `profile-picture` 
 * @property { string } id Media ID. Example: `123e4567-e89b-12d3-a456-426614174000` 
 * @property { string } url Url. Example: `host.com/media/images/123/profile.jpg` 
 * @property { object } meta Additional metadata. Example: `[object Object]` 
 * @property { boolean } uploaded Whether the media has been uploaded. Example: `true` 
 * @property { boolean } validated Whether the media has been validated. Example: `true` 
 * @property { string } provider Media provider. Example: `aws-s3` 
 */
export const MediaResponseSchema = z.object({ resourceName: z.string(), id: z.string(), url: z.string().nullish(), meta: z.object({}).nullish(), uploaded: z.boolean().nullish(), validated: z.boolean().nullish(), provider: z.string() });
export type MediaResponse = z.infer<typeof MediaResponseSchema>;


/** 
 * MediaValidatedResponseSchema 
 * @type { object }
 * @property { boolean } success Whether the media validation was successful. Example: `true` 
 * @property { MediaResponse } media The media object if validation was successful 
 */
export const MediaValidatedResponseSchema = z.object({ success: z.boolean(), media: MediaResponseSchema.nullish() });
export type MediaValidatedResponse = z.infer<typeof MediaValidatedResponseSchema>;


/** 
 * UpdateSkillDtoSchema 
 * @type { object }
 * @property { string } name Min Length: `1` 
 * @property { string } proficiency  
 */
export const UpdateSkillDtoSchema = z.object({ name: z.string().min(1), proficiency: CommonModels.ProficiencyEnumSchema });
export type UpdateSkillDto = z.infer<typeof UpdateSkillDtoSchema>;


/** 
 * UpdateSkillsDtoSchema 
 * @type { object }
 * @property { UpdateSkillDto[] } skills  
 */
export const UpdateSkillsDtoSchema = z.object({ skills: z.array(UpdateSkillDtoSchema) });
export type UpdateSkillsDto = z.infer<typeof UpdateSkillsDtoSchema>;


/** 
 * ProfileSkillDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } proficiency  
 */
export const ProfileSkillDtoSchema = z.object({ name: z.string(), proficiency: CommonModels.ProficiencyEnumSchema });
export type ProfileSkillDto = z.infer<typeof ProfileSkillDtoSchema>;


/** 
 * ProfileSkillsResponseDtoSchema 
 * @type { object }
 * @property { ProfileSkillDto[] } skills  
 */
export const ProfileSkillsResponseDtoSchema = z.object({ skills: z.array(ProfileSkillDtoSchema) });
export type ProfileSkillsResponseDto = z.infer<typeof ProfileSkillsResponseDtoSchema>;


/** 
 * UpdatePersonalInfoSocialMediaDtoSchema 
 * @type { object }
 * @property { string } linkedin  
 * @property { string } instagram  
 * @property { string } youtube  
 * @property { string } facebook  
 */
export const UpdatePersonalInfoSocialMediaDtoSchema = z.object({ linkedin: z.string().nullable(), instagram: z.string().nullable(), youtube: z.string().nullable(), facebook: z.string().nullable() });
export type UpdatePersonalInfoSocialMediaDto = z.infer<typeof UpdatePersonalInfoSocialMediaDtoSchema>;


/** 
 * UpdatePersonalInfoDtoSchema 
 * @type { object }
 * @property { string } jobPosition  
 * @property { string } pronoun  
 * @property { string } firstName  
 * @property { string } lastName  
 * @property { string } email  
 * @property { string } phone Min Length: `1` 
 * @property { string } phoneCountry Min Length: `2`. Max Length: `2` 
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country Min Length: `2`. Max Length: `2` 
 * @property { UpdatePersonalInfoSocialMediaDto } socialMedia  
 */
export const UpdatePersonalInfoDtoSchema = z.object({ jobPosition: z.string().nullish(), pronoun: z.string().nullable(), firstName: z.string().nullable(), lastName: z.string().nullable(), email: z.email().nullable(), phone: z.string().min(1), phoneCountry: z.string().min(2).max(2).nullish(), city: z.string().nullish(), state: z.string().max(2).nullish(), country: z.string().min(2).max(2).nullish(), socialMedia: UpdatePersonalInfoSocialMediaDtoSchema.nullish() });
export type UpdatePersonalInfoDto = z.infer<typeof UpdatePersonalInfoDtoSchema>;


/** 
 * UpdateWorkExperienceCompanyDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } industry  
 * @property { number } salary  
 * @property { string } start_date  
 * @property { string } end_date  
 * @property { boolean } is_current  
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country Min Length: `2`. Max Length: `2` 
 * @property { string } role_title  
 * @property { string } employment_type  
 * @property { string } description  
 * @property { string } work_setting  
 */
export const UpdateWorkExperienceCompanyDtoSchema = z.object({ name: z.string().nullable(), industry: CommonModels.CompanyIndustryEnumSchema.nullish(), salary: z.number().nullable(), start_date: z.string().nullable(), end_date: z.string().nullable(), is_current: z.boolean(), city: z.string().nullable(), state: z.string().max(2).nullable(), country: z.string().min(2).max(2).nullable(), role_title: z.string().nullable(), employment_type: CommonModels.EmploymentTypeEnumSchema.nullable(), description: z.string().nullable(), work_setting: CommonModels.WorkSettingEnumSchema.nullish() });
export type UpdateWorkExperienceCompanyDto = z.infer<typeof UpdateWorkExperienceCompanyDtoSchema>;


/** 
 * UpdateWorkExperienceDtoSchema 
 * @type { object }
 * @property { UpdateWorkExperienceCompanyDto[] } companies  
 */
export const UpdateWorkExperienceDtoSchema = z.object({ companies: z.array(UpdateWorkExperienceCompanyDtoSchema) });
export type UpdateWorkExperienceDto = z.infer<typeof UpdateWorkExperienceDtoSchema>;


/** 
 * UpdateEducationDtoSchema 
 * @type { object }
 * @property { string } start_date  
 * @property { string } end_date  
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country Min Length: `2`. Max Length: `2` 
 * @property { string } degree  
 * @property { string } field_of_study  
 * @property { string } institution_name  
 * @property { string } description  
 * @property { string } grade  
 */
export const UpdateEducationDtoSchema = z.object({ start_date: z.string().nullable(), end_date: z.string().nullable(), city: z.string().nullable(), state: z.string().max(2).nullable(), country: z.string().min(2).max(2).nullable(), degree: z.string(), field_of_study: z.string().nullable(), institution_name: z.string().nullable(), description: z.string().nullable(), grade: z.string().nullable() });
export type UpdateEducationDto = z.infer<typeof UpdateEducationDtoSchema>;


/** 
 * UpdateEducationsDtoSchema 
 * @type { object }
 * @property { UpdateEducationDto[] } educations  
 */
export const UpdateEducationsDtoSchema = z.object({ educations: z.array(UpdateEducationDtoSchema) });
export type UpdateEducationsDto = z.infer<typeof UpdateEducationsDtoSchema>;


/** 
 * UpdateVolunteerDtoSchema 
 * @type { object }
 * @property { string } organization_name  
 * @property { string } role  
 * @property { string } description  
 */
export const UpdateVolunteerDtoSchema = z.object({ organization_name: z.string(), role: z.string(), description: z.string().nullable() });
export type UpdateVolunteerDto = z.infer<typeof UpdateVolunteerDtoSchema>;


/** 
 * UpdateVolunteersDtoSchema 
 * @type { object }
 * @property { UpdateVolunteerDto[] } volunteers  
 */
export const UpdateVolunteersDtoSchema = z.object({ volunteers: z.array(UpdateVolunteerDtoSchema) });
export type UpdateVolunteersDto = z.infer<typeof UpdateVolunteersDtoSchema>;


/** 
 * UpdateCertificationDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } issuing_organization  
 * @property { string } issue_date  
 * @property { string } verification_url  
 * @property { string } credential_id  
 */
export const UpdateCertificationDtoSchema = z.object({ name: z.string(), issuing_organization: z.string().nullable(), issue_date: z.string().nullable(), verification_url: z.string().nullable(), credential_id: z.string().nullable() });
export type UpdateCertificationDto = z.infer<typeof UpdateCertificationDtoSchema>;


/** 
 * UpdateCertificationsDtoSchema 
 * @type { object }
 * @property { UpdateCertificationDto[] } licenses_or_certifications  
 */
export const UpdateCertificationsDtoSchema = z.object({ licenses_or_certifications: z.array(UpdateCertificationDtoSchema) });
export type UpdateCertificationsDto = z.infer<typeof UpdateCertificationsDtoSchema>;


/** 
 * UpdateAchievementDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } date  
 * @property { string } category  
 * @property { string } description  
 * @property { string } issuer  
 */
export const UpdateAchievementDtoSchema = z.object({ title: z.string(), date: z.string().nullable(), category: CommonModels.AchievementCategoryEnumSchema.nullable(), description: z.string().nullable(), issuer: z.string() });
export type UpdateAchievementDto = z.infer<typeof UpdateAchievementDtoSchema>;


/** 
 * UpdateAchievementsDtoSchema 
 * @type { object }
 * @property { UpdateAchievementDto[] } achievements_or_awards  
 */
export const UpdateAchievementsDtoSchema = z.object({ achievements_or_awards: z.array(UpdateAchievementDtoSchema) });
export type UpdateAchievementsDto = z.infer<typeof UpdateAchievementsDtoSchema>;


/** 
 * MediaUploadRequestSchema 
 * @type { object }
 * @property { string } resourceName Resource name supported by the system 
 * @property { string } fileName Original filename. Example: `profile.png` 
 * @property { number } fileSize Original file size in bytes. Minimum: `1`. Example: `1024` 
 * @property { string } mimeType File mime type. Example: `image/png` 
 * @property { string } method Upload method. Example: `put` 
 */
export const MediaUploadRequestSchema = z.object({ resourceName: z.string().nullish(), fileName: z.string(), fileSize: z.number().gte(1), mimeType: z.string().nullish(), method: z.string().nullish() });
export type MediaUploadRequest = z.infer<typeof MediaUploadRequestSchema>;


/** 
 * MediaAttachRequestDtoSchema 
 * @type { object }
 * @property { string } id Media Id 
 */
export const MediaAttachRequestDtoSchema = z.object({ id: z.uuid() });
export type MediaAttachRequestDto = z.infer<typeof MediaAttachRequestDtoSchema>;


/** 
 * GenerateBioResponseDtoSchema 
 * @type { object }
 * @property { string } bio The generated bio text 
 */
export const GenerateBioResponseDtoSchema = z.object({ bio: z.string() });
export type GenerateBioResponseDto = z.infer<typeof GenerateBioResponseDtoSchema>;


/** 
 * UpdatePositionInfoDtoSchema 
 * @type { object }
 * @property { string } jobPosition  
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country Min Length: `2`. Max Length: `2` 
 */
export const UpdatePositionInfoDtoSchema = z.object({ jobPosition: z.string().nullable(), city: z.string().nullable(), state: z.string().max(2).nullable(), country: z.string().min(2).max(2) });
export type UpdatePositionInfoDto = z.infer<typeof UpdatePositionInfoDtoSchema>;


}
